import { useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback, useMemo } from 'react';
import type { QuestionFilters } from '../types';
import { clearLastFilter, saveLastFilter } from './questionsFilterStorage';

const TAXONOMY_LEVELS = [
  'curriculumId',
  'versionId',
  'classId',
  'subjectId',
  'chapterId',
  'topicId',
  'subtopicId',
] as const;

/**
 * Merge a partial filter patch into a base. Changing (or clearing) a taxonomy
 * level clears every level below it, unless the patch sets that level too.
 * Pure — used by both the applied (URL) layer here and the draft layer in
 * QuestionsLayout.
 */
export function mergeFilters(
  base: QuestionFilters,
  next: Partial<QuestionFilters>,
): QuestionFilters {
  const merged: QuestionFilters = { ...base };
  const changed = TAXONOMY_LEVELS.findIndex((k) => k in next && next[k] !== base[k]);
  if (changed !== -1) {
    for (const k of TAXONOMY_LEVELS.slice(changed + 1)) merged[k] = undefined;
  }
  return Object.assign(merged, next);
}

/**
 * Drop every taxonomy level whose parent is unset, so a URL or stored filter
 * never carries a pick the rail's cascade could not have produced. Ancestors are
 * not filled from the tree: subject ↔ class is M:N, so a subject alone can't name its class.
 */
export function normalizeTaxonomy(filters: QuestionFilters): QuestionFilters {
  const f: QuestionFilters = { ...filters };
  TAXONOMY_LEVELS.forEach((level, i) => {
    const parent = TAXONOMY_LEVELS[i - 1];
    if (parent && !f[parent]) f[level] = undefined;
  });
  return f;
}

/** Build a clean search object (drops empty values) for navigation. */
export function toSearch(f: QuestionFilters): Record<string, unknown> {
  const s: Record<string, unknown> = {};
  if (f.curriculumId) s.curriculumId = f.curriculumId;
  if (f.versionId) s.versionId = f.versionId;
  if (f.classId) s.classId = f.classId;
  if (f.subjectId) s.subjectId = f.subjectId;
  if (f.chapterId) s.chapterId = f.chapterId;
  if (f.topicId) s.topicId = f.topicId;
  if (f.subtopicId) s.subtopicId = f.subtopicId;
  if (f.category) s.category = f.category;
  if ((f.types?.length ?? 0) > 0) s.types = f.types;
  if (f.hardness) s.hardness = f.hardness;
  if (f.importance) s.importance = f.importance;
  if (f.standard) s.standard = f.standard;
  if (f.standardId) s.standardId = f.standardId;
  return s;
}

/** Content equality of two filter sets (order-insensitive for `types`). */
export function filtersEqual(a: QuestionFilters, b: QuestionFilters): boolean {
  const norm = (f: QuestionFilters) =>
    JSON.stringify({
      curriculumId: f.curriculumId || undefined,
      versionId: f.versionId || undefined,
      classId: f.classId || undefined,
      subjectId: f.subjectId || undefined,
      chapterId: f.chapterId || undefined,
      topicId: f.topicId || undefined,
      subtopicId: f.subtopicId || undefined,
      category: f.category || undefined,
      hardness: f.hardness || undefined,
      importance: f.importance || undefined,
      standard: f.standard || undefined,
      standardId: f.standardId || undefined,
      types: [...(f.types ?? [])].sort(),
    });
  return norm(a) === norm(b);
}

/**
 * URL-driven applied-filter state for the pool browser.
 *
 * Source of truth: URL search params (via TanStack Router `useSearch`). These
 * are the *applied* filters — what the query actually fetches. The rail edits a
 * separate draft (see QuestionsLayout) and only commits here on Apply.
 * localStorage mirrors the last non-empty applied scope so returning to
 * /questions without explicit params restores the curator's last working scope
 * (seeded in `beforeLoad` of the route, not here).
 */
export function useQuestionFilters() {
  const search = useSearch({ from: '/_admin/questions' });
  const navigate = useNavigate();

  const filters: QuestionFilters = useMemo(
    () =>
      normalizeTaxonomy({
        curriculumId: search.curriculumId,
        versionId: search.versionId,
        classId: search.classId,
        subjectId: search.subjectId,
        chapterId: search.chapterId,
        topicId: search.topicId,
        subtopicId: search.subtopicId,
        category: search.category,
        types: search.types,
        hardness: search.hardness,
        importance: search.importance,
        standard: search.standard,
        standardId: search.standardId,
      }),
    [
      search.curriculumId,
      search.versionId,
      search.classId,
      search.subjectId,
      search.chapterId,
      search.topicId,
      search.subtopicId,
      search.category,
      search.types,
      search.hardness,
      search.importance,
      search.standard,
      search.standardId,
    ],
  );

  /** Immediate apply of a partial patch (used by active-filter chips). */
  const handleFilterChange = useCallback(
    (next: Partial<QuestionFilters>) => {
      const merged = mergeFilters(filters, next);
      saveLastFilter(merged);
      void navigate({ to: '/questions', search: toSearch(merged), replace: true });
    },
    [filters, navigate],
  );

  /** Commit a complete filter set → applied (used by the rail's Apply button). */
  const commitFilters = useCallback(
    (full: QuestionFilters) => {
      saveLastFilter(full);
      void navigate({ to: '/questions', search: toSearch(full), replace: true });
    },
    [navigate],
  );

  const handleReset = useCallback(() => {
    clearLastFilter();
    void navigate({ to: '/questions', search: {}, replace: true });
  }, [navigate]);

  return { filters, handleFilterChange, commitFilters, handleReset };
}
